import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Modal, Loading } from "@/components/atoms";
import { DataTable, Pagination } from "@/components/molecules";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { usePagination, useSorting } from "@/hooks";
import {
  useDeleteVehicleTypeMutation,
  useReorderVehicleTypesMutation,
  useToggleVehicleTypeMutation,
  vehicleTypeApi,
} from "@/redux/apis/config/trip/vehicleType/vehicleTypeApi";
import { useAppDispatch } from "@/redux/hooks";
import { VehicleType } from "@/redux/slices/vehicle/types";
import { Page } from "@/redux/types";
import { addNotification, clsx } from "@/utils";
import { DuplicateConfirmModal } from "./DuplicateConfirmModal";
import { modals } from "./fixtures";
import { generateColumns, generateTableData } from "./helpers";
import { useGetVehicleTypesParams } from "./hooks/useGetVehicleTypesParams";
import { VehicleTypesEditorTableData } from "./types";

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  items: VehicleType[];
}

export const VehicleTypesTableEditor = ({ items, className, ...props }: Props) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { params } = useGetVehicleTypesParams();
  const { sorting, setSorting } = useSorting();
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [pendingCopyItem, setPendingCopyItem] = useState<VehicleType | null>(null);
  const [toggle, { isLoading: toggleLoading, error: toggleError }] = useToggleVehicleTypeMutation();
  const [remove, { isLoading: removeLoading, error: deleteError, reset: resetDelete }] = useDeleteVehicleTypeMutation();
  const [reorder, { isLoading: reorderLoading, error: reorderError }] = useReorderVehicleTypesMutation();
  const [activeModal, setActiveModal] = useState<"error" | "exit" | "delete" | null>(null);
  const isLoading = toggleLoading || removeLoading || reorderLoading;
  const error = toggleError ?? deleteError ?? reorderError;

  const onEdit = useCallback((item: VehicleType) => navigate(`./${item.uuid}`), [navigate]);

  const onCopy = useCallback((item: VehicleType) => setPendingCopyItem(item), []);

  const handleCopyConfirm = useCallback(() => {
    const item = pendingCopyItem;
    if (!item) return;
    setPendingCopyItem(null);
    navigate("./add", {
      state: {
        template: {
          name: item.name,
          capacity: item.vehicleCapacity.pax,
          bags: item.vehicleCapacity.bags,
          waitTimeCost: item.waitTimeCost / 100,
          costAdjustment: item.perDistanceUnitMultiplier,
          customCommissionEnabled: item.customCommission.enabled,
          customCommissionRate: item.customCommission.rate,
          vehicleCategoryUuid: item.vehicleCategory?.uuid ?? "",
        },
        templateOriginUuid: item.uuid,
        templateOriginName: item.name,
      },
    });
  }, [navigate, pendingCopyItem]);

  const onToggle = useCallback(
    (id: string, active: boolean) => {
      const item = items.find((i) => i.uuid === id);
      if (!item) return;
      toggle({ id, active })
        .unwrap()
        .then(() => {
          dispatch(
            vehicleTypeApi.util.updateQueryData("getVehicleTypes", params, (draft) => {
              const row = draft.vehicleTypes.find((i) => i.uuid === id);
              if (row) row.isActive = active;
            })
          );
          addNotification("info", `Changed active status: ${active ? "On" : "Off"}`, `Changed active status for "${item.name}".`);
        })
        .catch(() => setActiveModal("error"));
    },
    [dispatch, items, params, toggle]
  );

  const onReorder = useCallback(
    (sortedItems: VehicleTypesEditorTableData[]) => {
      const mapping = sortedItems.reduce((acc, item, index) => {
        acc[item.id] = index;
        return acc;
      }, {} as Record<string, number>);

      dispatch(
        vehicleTypeApi.util.updateQueryData("getVehicleTypes", params, (draft) => {
          const newItems = items.map((item) => ({ ...item, orderScore: mapping[item.uuid] })).sort((a, b) => a.orderScore - b.orderScore);
          Object.assign(draft.vehicleTypes, newItems);
        })
      );

      reorder(mapping)
        .unwrap()
        .then(() => addNotification("info", "Updated Order", "Successfully updated vehicle type order."))
        .catch(() => setActiveModal("error"));
    },
    [dispatch, items, params, reorder]
  );

  const onDelete = useCallback(
    (item: VehicleType) => {
      resetDelete();
      setPendingDeleteId(item.uuid);
      setActiveModal("delete");
    },
    [resetDelete]
  );

  const handleDelete = useCallback(() => {
    setActiveModal(null);
    const item = items.find((i) => i.uuid === pendingDeleteId);
    if (!item) return;
    remove(item.uuid)
      .unwrap()
      .then(() => {
        dispatch(
          vehicleTypeApi.util.updateQueryData("getVehicleTypes", params, (draft) => {
            draft.vehicleTypes = draft.vehicleTypes.filter((i) => i.uuid !== item.uuid);
          })
        );
        addNotification("info", "Deleted 1 row", `Deleted "${item.name}"`);
      })
      .catch(() => setActiveModal("error"));
  }, [dispatch, items, params, pendingDeleteId, remove]);

  const renderModalButtons = () => {
    switch (activeModal) {
      case "delete":
        return (
          <>
            <Button variant="secondary" onClick={() => setActiveModal(null)} className="px-8">
              Cancel
            </Button>
            <Button variant="primary" onClick={handleDelete} className="bg-danger px-8 hover:bg-danger-dark">
              Delete
            </Button>
          </>
        );
      default:
        return (
          <Button variant="primary" onClick={() => setActiveModal(null)} className="px-8">
            Back
          </Button>
        );
    }
  };

  const renderModal = () => {
    if (!activeModal) return null;
    let title = modals[activeModal].title;
    let description = modals[activeModal].description;
    if (activeModal === "error") {
      const apiMessage = error ? getErrorMessages(error)[0] ?? "" : "";
      description = apiMessage;
      if (deleteError) {
        if (/open jobs/i.test(apiMessage)) {
          title = modals.deleteErrorOpenJobs.title;
          description = modals.deleteErrorOpenJobs.description;
        } else if (/attached to vehicles/i.test(apiMessage)) {
          title = modals.deleteErrorVehicles.title;
          description = modals.deleteErrorVehicles.description;
        }
      }
    }
    return (
      <Modal
        open
        title={title}
        description={description}
        onClose={() => setActiveModal(null)}
        renderButtons={renderModalButtons}
      />
    );
  };

  const data = useMemo(() => generateTableData(items, { onToggle, onEdit, onDelete, onCopy }), [items, onCopy, onDelete, onEdit, onToggle]);
  const columns = useMemo(() => generateColumns(data), [data]);

  return (
    <div className="relative">
      {renderModal()}
      <DuplicateConfirmModal
        open={!!pendingCopyItem}
        onClose={() => setPendingCopyItem(null)}
        onConfirm={handleCopyConfirm}
      />
      {isLoading && <Loading className="rounded-lg" />}
      <DataTable
        columns={columns}
        data={data}
        className={clsx("w-full overflow-x-auto rounded-b-none bg-white [&_table]:table-auto", className)}
        onReorder={onReorder}
        sorting={sorting}
        setSorting={setSorting}
        {...props}
      />
    </div>
  );
};

const VehicleTypesTableEditorPagination = ({ page }: { page: Page }) => {
  const { current, size, total, count } = page;
  const { handlePageClick, handleSizeChange } = usePagination("config-vehicle-types", 30);
  return (
    <div className="rounded-b-lg bg-white px-3 py-2 ring-1 ring-neutral-mid-gray">
      <Pagination
        onPageClick={handlePageClick}
        onSizeChange={handleSizeChange}
        currentPage={current}
        pageSize={size}
        totalItems={total}
        pageCount={count}
      />
    </div>
  );
};

VehicleTypesTableEditor.Pagination = VehicleTypesTableEditorPagination;
